/**
 * Editor de ajustes por formulario (27/10, Gabor: «solo veo códigos»). Pinta cualquier documento de
 * `settings/*` a partir de su propio contenido, sin JSON a la vista:
 *   - importe `{ amount, currency }` → «12,50 €» con decimales y moneda (guarda la unidad menor);
 *   - texto por idioma `{ es, en, hu… }` → el español a la vista, el resto plegado;
 *   - sí/no → interruptor; número → número; texto largo → caja grande;
 *   - lista de textos → una línea por elemento; lista de objetos → tarjetas con «Añadir» / «Quitar»;
 *   - objeto → grupo con título.
 * Las etiquetas salen de LABELS (español) y, si no hay, de la propia clave legible.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { LANGUAGES } from '@nuvvu/i18n';
import { CURRENCIES, CURRENCY_MINOR_DIGITS } from '@nuvvu/shared';
import { tokens } from '@nuvvu/ui';
import { btn, field, muted } from './ui.js';

type Json = unknown;
type Obj = Record<string, Json>;

/** Etiquetas en español de las claves de `settings/*` (lo que no esté, se humaniza). */
export const LABELS: Record<string, string> = {
  // fees
  protection_fee_percent: 'Protección al comprador (%)',
  protection_fee_fixed: 'Protección al comprador (fijo)',
  protection_fee_waived_above: 'Sin protección por encima de',
  optional_protection_percent: 'Protección opcional (%)',
  transport_surcharge: 'Recargo de transporte',
  handover_fee: 'Tarifa entrega en mano',
  withdrawal_min: 'Retirada mínima',
  withdrawal_auto_approve_max: 'Aprobar retiradas solas hasta',
  instant_withdrawal_fee_percent: 'Retirada instantánea (%)',
  instant_withdrawals_enabled: 'Retirada instantánea activada (solo cuando Stripe la habilite)',
  instant_withdrawal_min_fee: 'Comisión mínima de la retirada instantánea',
  fx_withdrawal_surcharge_percent: 'Recargo por cambio de moneda al retirar (%)',
  max_offer_discount_percent: 'Descuento máximo en ofertas (%)',
  max_bundle_discount_percent: 'Descuento máximo en lotes (%)',
  dispute_return_days: 'Días para devolver en una incidencia',
  dispute_seller_response_hours: 'Horas para que responda el vendedor',
  dispute_window_hours: 'Horas para abrir incidencia tras la entrega',
  dispute_resolution_hours: 'Horas para resolver la incidencia',
  accept_window_hours: 'Horas para aceptar la venta',
  ship_window_hours: 'Horas para enviar',
  wallet_payment_waives_fixed: 'Pagar con saldo quita la parte fija (sin efecto desde 27/10)',
  fee_fx_rates: 'Tipos de cambio de respaldo (por 1 €)',
  club_tiers: 'Niveles del club',
  // club (settings/club, 2.1)
  annual_price: 'Precio al año',
  trial_days: 'Días gratis la primera vez',
  points_bps: 'Puntos (centésimas de %: 100 = 1 % del artículo)',
  welcome_credit: 'Bienvenida en puntos',
  waive_fixed_protection: 'Sin parte fija de la protección',
  fixed_waiver_monthly_cap: 'Compras al mes sin parte fija (tope)',
  extra_claim_hours: 'Horas de más para reclamar',
  early_offer_days: 'Días antes en las ofertas',
  alert_lead_hours: 'Horas antes en los avisos',
  savings_guarantee: 'Garantía de ahorro el primer año',
  min_purchases: 'Compras mínimas',
  discount_points: 'Puntos de descuento',
  level_perks: 'Ventajas por nivel de vendedor',
  seller_penalty: 'Penalización al vendedor',
  cancellations: 'Cancelaciones',
  window_days: 'Ventana (días)',
  pause_days: 'Días de pausa',
  // proPlans
  plans: 'Planes',
  tier: 'Nivel',
  legacy_key: 'Clave de la 1.0',
  name: 'Nombre',
  monthly_price: 'Precio al mes',
  stripe_price_id: 'Id de precio en Stripe',
  app_store_product_id: 'Id en App Store',
  play_store_product_id: 'Id en Google Play',
  photo_limit: 'Fotos por anuncio',
  featured_per_month: 'Destacados incluidos al mes (vacío = sin límite)',
  ai_studio_per_month: 'Fotos con IA al mes',
  benefits: 'Ventajas (claves de texto)',
  active: 'Activo',
  free_photo_limit: 'Fotos por anuncio sin PRO',
  free_bumps_per_month: 'Subidas gratis al mes',
  pro_spotlight_days: 'Días de escaparate incluidos',
  // featuredPrices
  featured: 'Destacados',
  bumps: 'Subidas',
  spotlight: 'Escaparates',
  code: 'Código',
  price: 'Precio',
  days: 'Días',
  scope: 'Alcance (one = su país, all = toda Europa)',
  homepage: 'En portada',
  auto_days: 'Días de subida automática',
  // countries
  countries: 'Países',
  default_language: 'Idioma por defecto',
  languages: 'Idiomas',
  currency: 'Moneda',
  eu_member: 'Miembro de la UE',
  phone_prefix: 'Prefijo telefónico',
  timezone: 'Zona horaria',
  signup_enabled: 'Se permite registrarse',
  buying_enabled: 'Se permite comprar',
  selling_enabled: 'Se permite vender',
  customs_required: 'Aduana necesaria',
  payment_methods: 'Métodos de pago',
  blocked_categories: 'Categorías bloqueadas',
  // company
  legal_name: 'Razón social',
  vat_number: 'NIF',
  eu_vat_id: 'NIF-IVA intracomunitario',
  address: 'Dirección',
  support_email: 'Correo de soporte',
  admin_alert_emails: 'Correos que reciben los avisos del admin',
  sender_email: 'Remitente de los correos',
  dsa_contact_email: 'Correo de contacto DSA',
  legal_representative: 'Representante legal',
  registry_page: 'Registro: hoja',
  registry_sheet: 'Registro: folio',
  registry_volume: 'Registro: tomo',
  founded: 'Fundada',
  jurisdiction: 'Jurisdicción',
  website: 'Web',
  social: 'Redes sociales',
  fr_mediator_name: 'Mediador (Francia): nombre',
  fr_mediator_address: 'Mediador (Francia): dirección',
  fr_mediator_website: 'Mediador (Francia): web',
  // featureFlags
  flags: 'Interruptores',
  // legal
  documents: 'Documentos',
  version: 'Versión',
  content: 'Texto',
  published_at: 'Publicado',
  // socialPublisher / adServer
  enabled: 'Activado',
  facebook_enabled: 'Publicar en Facebook',
  instagram_enabled: 'Publicar en Instagram',
  page_id: 'Id de la página de Facebook',
  ig_user_id: 'Id de Instagram',
  ig_link_comment: 'Comentario con enlace en Instagram',
  hashtags: 'Hashtags',
  tone: 'Tono',
  hours: 'Horas de publicación',
  mix: 'Mezcla',
  last_lang: 'Último idioma',
  last_run_slot: 'Último turno',
  feed_frequency: 'Un anuncio cada N productos',
  feed_boost: 'Impulso en el feed',
  // environment
  env: 'Entorno',
  maintenance: 'Modo mantenimiento',
  maintenance_message: 'Mensaje de mantenimiento',
  min_app_version: 'Versión mínima de las apps',
  // announcement
  title: 'Título',
  body: 'Texto',
  cta_label: 'Texto del botón',
  cta_url: 'Enlace del botón',
  close_label: 'Texto de cerrar',
  dismissible: 'Se puede cerrar',
  audience: 'A quién',
  severity: 'Tipo',
  starts_at: 'Desde',
  ends_at: 'Hasta',
  // genéricos
  amount: 'Importe',
  id: 'Id',
  listing: 'Anuncio',
  seller: 'Vendedor',
  min_price: 'Precio mínimo',
};

const human = (k: string): string =>
  LABELS[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ');

const isObj = (v: Json): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const LANG_CODES = new Set<string>(LANGUAGES as readonly string[]);
const isMoney = (v: Json): v is { amount: number; currency: string } =>
  isObj(v) &&
  typeof v.amount === 'number' &&
  typeof v.currency === 'string' &&
  Object.keys(v).length === 2;
const isLocalized = (v: Json): v is Record<string, string> =>
  isObj(v) &&
  Object.keys(v).length > 0 &&
  Object.keys(v).every((k) => LANG_CODES.has(k)) &&
  Object.values(v).every((x) => typeof x === 'string');

const box = {
  border: `1px solid ${tokens.color.border}`,
  borderRadius: tokens.radius.lg,
  padding: 12,
  display: 'grid',
  gap: 10,
} as const;
const row = { display: 'grid', gap: 4, fontSize: 13 } as const;

function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <label style={row}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

function MoneyField({
  value,
  onChange,
}: {
  value: { amount: number; currency: string };
  onChange: (v: Json) => void;
}): ReactElement {
  const digits = (CURRENCY_MINOR_DIGITS as Record<string, number | undefined>)[value.currency] ?? 2;
  const [text, setText] = useState(String(value.amount / 10 ** digits).replace('.', ','));
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        style={{ ...field, maxWidth: 160 }}
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value.replace(/\s/g, '').replace(',', '.'));
          if (Number.isFinite(n)) onChange({ ...value, amount: Math.round(n * 10 ** digits) });
        }}
      />
      <select
        style={{ ...field, width: 'auto' }}
        value={value.currency}
        onChange={(e) => {
          onChange({ ...value, currency: e.target.value });
        }}
      >
        {(CURRENCIES as readonly string[]).map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
  );
}

function LocalizedField({
  value,
  onChange,
  long,
}: {
  value: Record<string, string>;
  onChange: (v: Json) => void;
  long: boolean;
}): ReactElement {
  const others = Object.keys(value).filter((k) => k !== 'es');
  const input = (lang: string): ReactElement =>
    long ? (
      <textarea
        style={{ ...field, minHeight: 120 }}
        value={value[lang] ?? ''}
        onChange={(e) => {
          onChange({ ...value, [lang]: e.target.value });
        }}
      />
    ) : (
      <input
        style={field}
        value={value[lang] ?? ''}
        onChange={(e) => {
          onChange({ ...value, [lang]: e.target.value });
        }}
      />
    );
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {input('es')}
      {others.length > 0 && (
        <details>
          <summary style={{ ...muted, cursor: 'pointer' }}>
            Otros idiomas ({others.length}) — si los dejas vacíos se traducen del español al guardar
            donde esté previsto
          </summary>
          <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
            {others.map((l) => (
              <div key={l} style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 6 }}>
                <span style={{ ...muted, paddingTop: 8 }}>{l}</span>
                {input(l)}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/** Campo de cualquier tipo, recursivo. `k` es la clave (para la etiqueta y para decidir el control). */
export function Field({
  k,
  value,
  onChange,
  depth = 0,
}: {
  k: string;
  value: Json;
  onChange: (v: Json) => void;
  depth?: number;
}): ReactElement {
  const label = human(k);
  if (typeof value === 'boolean')
    return (
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
          style={{ width: 18, height: 18 }}
        />
        <span style={{ fontWeight: 600 }}>{label}</span>
      </label>
    );
  if (typeof value === 'number')
    return (
      <Row label={label}>
        <input
          style={{ ...field, maxWidth: 200 }}
          type="number"
          step="any"
          value={value}
          onChange={(e) => {
            onChange(e.target.value === '' ? 0 : Number(e.target.value));
          }}
        />
      </Row>
    );
  if (typeof value === 'string' || value === null) {
    const s = value ?? '';
    const long = s.length > 80 || s.includes('\n');
    return (
      <Row label={label}>
        {long ? (
          <textarea
            style={{ ...field, minHeight: 100 }}
            value={s}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        ) : (
          <input
            style={field}
            value={s}
            placeholder={value === null ? 'vacío' : undefined}
            onChange={(e) => {
              onChange(value === null && e.target.value === '' ? null : e.target.value);
            }}
          />
        )}
      </Row>
    );
  }
  if (isMoney(value))
    return (
      <Row label={label}>
        <MoneyField value={value} onChange={onChange} />
      </Row>
    );
  if (isLocalized(value))
    return (
      <Row label={`${label} (español)`}>
        <LocalizedField
          value={value}
          onChange={onChange}
          long={k === 'content' || k === 'body' || Object.values(value).some((x) => x.length > 80)}
        />
      </Row>
    );
  if (Array.isArray(value)) {
    const list = value as Json[];
    const primitives = list.every((x) => typeof x === 'string' || typeof x === 'number');
    if (primitives) {
      const numbers = list.length > 0 && list.every((x) => typeof x === 'number');
      return (
        <Row label={`${label} (uno por línea)`}>
          <textarea
            style={{ ...field, minHeight: 60 }}
            value={list.map(String).join('\n')}
            onChange={(e) => {
              const parts = e.target.value.split('\n').map((x) => x.trim());
              onChange(
                parts
                  .filter((x, i) => x !== '' || i < parts.length - 1)
                  .filter(Boolean)
                  .map((x) => (numbers ? Number(x) : x)),
              );
            }}
          />
        </Row>
      );
    }
    return (
      <fieldset style={{ ...box, margin: 0 }}>
        <legend style={{ fontWeight: 700, fontSize: 14 }}>
          {label} ({list.length})
        </legend>
        {list.map((item, i) => (
          <div key={i} style={{ ...box, background: tokens.color.bg }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: 13 }}>
                {isObj(item) && typeof item.code === 'string'
                  ? item.code
                  : isObj(item) && typeof item.tier === 'string'
                    ? item.tier
                    : `#${String(i + 1)}`}
              </strong>
              <button
                type="button"
                style={btn(false, true)}
                onClick={() => {
                  if (window.confirm('¿Quitar este elemento?'))
                    onChange(list.filter((_, j) => j !== i));
                }}
              >
                Quitar
              </button>
            </div>
            <Field
              k={k}
              value={item}
              depth={depth + 1}
              onChange={(nv) => {
                onChange(list.map((x, j) => (j === i ? nv : x)));
              }}
            />
          </div>
        ))}
        {list.length > 0 && (
          <button
            type="button"
            style={{ ...btn(false), justifySelf: 'start' }}
            onClick={() => {
              onChange([...list, structuredClone(list[list.length - 1])]);
            }}
          >
            + Añadir (copia del último)
          </button>
        )}
      </fieldset>
    );
  }
  if (isObj(value)) {
    const body = (
      <div
        style={{
          display: 'grid',
          gap: 10,
          gridTemplateColumns: depth === 0 ? 'repeat(auto-fill, minmax(280px, 1fr))' : '1fr',
        }}
      >
        {Object.entries(value).map(([ck, cv]) => (
          <Field
            key={ck}
            k={ck}
            value={cv}
            depth={depth + 1}
            onChange={(nv) => {
              onChange({ ...value, [ck]: nv });
            }}
          />
        ))}
      </div>
    );
    if (depth === 0) return body;
    return (
      <details open={depth < 3} style={box}>
        <summary style={{ fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>{label}</summary>
        {body}
      </details>
    );
  }
  return <span style={muted}>{label}: (sin editar)</span>;
}

/** El documento entero: cada clave de primer nivel es un bloque. */
export function FormEditor({
  value,
  onChange,
}: {
  value: Obj;
  onChange: (v: Obj) => void;
}): ReactElement {
  const simple = Object.entries(value).filter(([, v]) => !isObj(v) && !Array.isArray(v));
  const complex = Object.entries(value).filter(([, v]) => isObj(v) || Array.isArray(v));
  const set = (k: string, v: Json): void => {
    onChange({ ...value, [k]: v });
  };
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {simple.length > 0 && (
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}
        >
          {simple.map(([k, v]) => (
            <Field
              key={k}
              k={k}
              value={v}
              onChange={(nv) => {
                set(k, nv);
              }}
            />
          ))}
        </div>
      )}
      {complex.map(([k, v]) => (
        <Field
          key={k}
          k={k}
          value={v}
          depth={1}
          onChange={(nv) => {
            set(k, nv);
          }}
        />
      ))}
    </div>
  );
}
